"use client";
import { useEffect, useState } from "react";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import Spinner from "../../../components/Spinner/index.js";

function ContentTasa({ dataUser }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [numberTasa, setNumberTasa] = useState("");
  const [fetching, setFetching] = useState(false);
  const [doneTasa, setDoneTasa] = useState(false);
  const [previousTasa, setPreviousTasa] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [nominalTasa, setNominalTasa] = useState("");

  const { data, isFetching } = useSnapshotWhere("tasa", {
    filter: {
      firstCondition: "majorCompanyId",
      secondCondition: "==",
      thirdCondition: dataUser[0]?.majorCompanyId,
    },
  });

  const { handleUpdate } = useUpdateDoc();

  // Implementación manual de la raíz 12 usando solo multiplicaciones
  const calcularNominalActual = (tea) => {
    const teaFloat = Number(tea);
    if (isNaN(teaFloat)) return "";

    const m = 12; // periodos de capitalización (mensual)

    // Fórmula: TNA = m * ((1 + TEA)^(1/m) - 1)
    // Donde TEA está en porcentaje, así que dividimos entre 100
    const base = 1 + teaFloat / 100;

    // Calcular la raíz m-ésima usando Math.pow (mucho más preciso)
    const factorMensual = Math.pow(base, 1 / m);

    const tasaMensual = factorMensual - 1;
    const tna = tasaMensual * m * 100;

    return tna;
  };

  useEffect(() => {
    setIsLoaded(true);
    if (!isFetching && data[0]) {
      const tasa = data[0]?.number || "";
      const tea = data[0]?.tea || "";
      setNumberTasa(tea);
      setPreviousTasa(tea);
      setNominalTasa(tasa);
    }
  }, [isFetching, data]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    // Permitir solo números y un punto decimal
    const sanitizedValue = value.replace(/[^0-9.]/g, "");
    const parts = sanitizedValue.split(".");

    // Asegurarse de que haya como máximo un punto decimal
    const finalValue =
      parts.length > 2 ? parts[0] + "." + parts[1] : sanitizedValue;
    setNumberTasa(finalValue);
    setNominalTasa(calcularNominalActual(finalValue));

    if (doneTasa) {
      setDoneTasa(false);
    }
  };

  const handleUpdateTasa = async () => {
    if (!numberTasa || numberTasa === previousTasa) return;

    try {
      setFetching(true);
      await handleUpdate("tasa", data[0]?.id, {
        number: Number(nominalTasa),
        tea: Number(numberTasa),
        lastUpdated: new Date().toISOString(),
        updatedBy: dataUser[0]?.name || "Usuario",
      });

      setPreviousTasa(numberTasa);
      setDoneTasa(true);

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setDoneTasa(false), 3000);
    } catch (error) {
      console.log(error);
    } finally {
      setFetching(false);
    }
  };

  const getTasaComparison = () => {
    const current = Number.parseFloat(numberTasa);
    const previous = Number.parseFloat(previousTasa);

    if (isNaN(current) || isNaN(previous) || current === previous) return null;

    const difference = current - previous;
    const isIncrease = difference > 0;

    return {
      difference: Math.abs(difference).toFixed(2),
      isIncrease,
      percentage: ((Math.abs(difference) / previous) * 100).toFixed(2),
    };
  };

  const comparison = getTasaComparison();

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4">
          <Spinner />
          <p className="text-gray-600">Cargando información de tasa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight justify-center flex items-center gap-3">
            <div className="p-2 bg-[#49b0a8]/10 rounded-xl">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M12 2L13.09 8.26L19 7L14.74 12.26L21 13.09L14.74 17.74L19 23L13.09 21.74L12 28L10.91 21.74L5 23L9.26 17.74L3 13.09L9.26 12.26L5 7L10.91 8.26L12 2Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            Gestión de Tasa de Interés
          </h1>
          <p className="text-gray-600">
            Administra la tasa efectiva anual que se aplicará a todos los
            créditos
          </p>
        </div>
      </div>

      {/* Current Rate Display */}
      <div
        className={`transition-all duration-700 ease-out delay-200 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-gradient-to-br from-[#49b0a8] to-[#3a8a84] rounded-xl shadow-sm p-6 text-white">
          <div className="flex gap-10 items-center justify-center">
            <div>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L13.09 8.26L19 7L14.74 12.26L21 13.09L14.74 17.74L19 23L13.09 21.74L12 28L10.91 21.74L5 23L9.26 17.74L3 13.09L9.26 12.26L5 7L10.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
                Tasa Efectiva Anual Actual
              </h2>
              <div className="text-4xl font-bold mb-2">
                {data[0]?.tea || "0.00"}%
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L13.09 8.26L19 7L14.74 12.26L21 13.09L14.74 17.74L19 23L13.09 21.74L12 28L10.91 21.74L5 23L9.26 17.74L3 13.09L9.26 12.26L5 7L10.91 8.26L12 2Z"
                    fill="currentColor"
                  />
                </svg>
                Tasa Nominal Anual
              </h2>
              <div className="text-4xl font-bold mb-2">
                {Number(nominalTasa).toFixed(2) || "0.00"}%
              </div>
            </div>
          </div>
          <p className="text-sm opacity-80">
            Última actualización:{" "}
            {data[0]?.lastUpdated
              ? new Date(data[0].lastUpdated).toLocaleDateString()
              : "No disponible"}
          </p>
        </div>
      </div>

      {/* Update Form */}
      <div
        className={`transition-all duration-700 ease-out delay-400 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 justify-center">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="text-[#49b0a8]"
            >
              <path
                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Actualizar Tasa de Interés
          </h2>

          <div className="space-y-6">
            {/* Success Message */}
            {doneTasa && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-green-600"
                  >
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-green-800 font-medium">
                    ¡Tasa actualizada correctamente!
                  </p>
                  <p className="text-green-700 text-sm">
                    La nueva tasa se aplicará a todos los créditos futuros
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="tasa"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Nueva Tasa Efectiva Anual (%)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="tasa"
                      name="tasa"
                      className="block w-full px-4 py-3 text-lg border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                      placeholder="Ej: 29.49"
                      value={numberTasa}
                      onChange={handleInputChange}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <span className="text-gray-500 text-lg">%</span>
                    </div>
                  </div>
                </div>

                {/* Comparison Display */}
                {/* {comparison && (
                  <div
                    className={`p-4 rounded-lg border ${
                      comparison.isIncrease
                        ? "bg-red-50 border-red-200"
                        : "bg-green-50 border-green-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={
                          comparison.isIncrease
                            ? "text-red-600"
                            : "text-green-600"
                        }
                      >
                        <path
                          d={
                            comparison.isIncrease
                              ? "M7 14l5-5 5 5"
                              : "M7 10l5 5 5-5"
                          }
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        className={`text-sm font-medium ${
                          comparison.isIncrease
                            ? "text-red-800"
                            : "text-green-800"
                        }`}
                      >
                        {comparison.isIncrease ? "Incremento" : "Reducción"} de{" "}
                        {comparison.difference}% ({comparison.percentage}%{" "}
                        {comparison.isIncrease ? "más alta" : "más baja"})
                      </span>
                    </div>
                  </div>
                )} */}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleUpdateTasa}
                    disabled={fetching || !numberTasa}
                    className={`
                    flex-1 group relative overflow-hidden px-6 py-3 rounded-xl font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95 flex items-center justify-center gap-2
                    ${
                      fetching || !numberTasa
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                    }
                  `}
                  >
                    {/* Background animation */}
                    {!fetching && numberTasa && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    )}

                    {fetching ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span className="relative z-10">Actualizando...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="relative z-10"
                        >
                          <path
                            d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="relative z-10">Actualizar Tasa</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setNumberTasa(previousTasa);
                      setNominalTasa(calcularNominalActual(previousTasa));
                    }}
                    disabled={fetching}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Restablecer
                  </button>
                </div>
              </div>

              {/* Info Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-blue-600"
                    >
                      <path
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-blue-800 font-medium mb-1">
                      Información importante
                    </h4>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>
                        • La nueva tasa se aplicará únicamente a créditos
                        futuros
                      </li>
                      <li>
                        • Los créditos existentes mantendrán su tasa original
                      </li>
                      <li>
                        • Se recomienda notificar a los usuarios sobre cambios
                        significativos
                      </li>
                      <li>
                        • El cambio quedará registrado en el historial del
                        sistema
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentTasa;
